import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./components/Sidebar.js";
import { MobileNav } from "./components/MobileNav.js";
import { TopBar } from "./components/TopBar.js";
import { AuthSplash } from "./components/SignInWithOpenMarkets.js";
import { UnlockExplainer, UpgradeReturnBanner } from "./components/Gate.js";
import { CapabilitiesModal } from "./components/CapabilitiesModal.js";
import { WelcomeTour } from "./components/WelcomeTour.js";
import { InstallBanner } from "./components/InstallBanner.js";
import { PageErrorBoundary } from "./components/ErrorBoundary.js";
import { useAuthStatus } from "./lib/omauth.js";
import { handleStoreReturn } from "./lib/store.js";
import { useLiveBalances, useLiveOrders, useLiveQueryBridge, useLiveSignals } from "./lib/ws.js";

/** Page title shown in the top bar, derived from the route. */
function titleFor(pathname: string): string {
  if (pathname === "/") return "Desk";
  if (pathname.startsWith("/markets")) return "Markets";
  if (pathname.startsWith("/portfolio") || pathname.startsWith("/orders")) return "Portfolio";
  if (pathname.startsWith("/history")) return "History";
  if (pathname.startsWith("/contests")) return "Contest";
  if (pathname.startsWith("/depth")) return "Depth";
  return "greenbook";
}

/** App shell: left sidebar nav + slim top bar + routed content. */
export function App() {
  // Bridge relay pushes → react-query cache (mounted once for the whole SPA).
  useLiveQueryBridge();
  // Keep the own-account orders channel subscribed app-wide so resting orders are
  // monitored (and the sidebar badge stays live) from any page.
  useLiveOrders();
  // Keep balances live app-wide: the `balance` channel pushes invalidate the account query
  // so displayed expected_balance updates in real time (no ~60s lag).
  useLiveBalances();
  // Keep the signals feed live app-wide (the contest page's edge/signals chip uses it).
  useLiveSignals();
  const { pathname } = useLocation();
  // Back from an in-app purchase checkout (`?om_order=`): re-read holdings (and close the popup).
  const qc = useQueryClient();
  useEffect(() => {
    void handleStoreReturn(qc);
  }, [qc]);
  // Mobile nav drawer (<lg); the desktop rail is hidden on small screens.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [pathname]); // close the drawer on navigation
  // No sign-in wall: everyone gets the shell. Signed out, Markets + Contest detail read the
  // org-keyed public feed and every account/paid view shows a Sign-in or Unlock card (see
  // components/Gate.tsx, main.tsx routes, and the per-scope `enabled` gates in queries.ts).
  // The live hooks above are no-ops until there's a credential (ws.ts won't open signed out).
  //
  // The one exception: while a sign-in callback completes in THIS window (redirect return, or
  // the popup's own leg before it self-closes) show a spinner, not the app.
  const { loading } = useAuthStatus();
  if (loading) return <AuthSplash />;
  return (
    <div className="flex h-full bg-bg text-ink">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar title={titleFor(pathname)} onMenu={() => setNavOpen(true)} />
        <main className="min-h-0 flex-1 overflow-auto">
          {/* A crashing page shows a recovery card instead of blanking the app; navigating away resets it. */}
          <PageErrorBoundary key={pathname}>
            <Outlet />
          </PageErrorBoundary>
        </main>
      </div>
      <MobileNav open={navOpen} onClose={() => setNavOpen(false)} />
      {/* First-run walkthrough (once per browser; replay from the top-bar "?"). */}
      <WelcomeTour />
      {/* Post-connect capability summary (auto once per key; re-openable from the key chip). */}
      <CapabilitiesModal />
      {/* Gentle "add to home screen" nudge (iOS Safari steps / Android install), snoozable. */}
      <InstallBanner />
      {/* After a redirect-mode plan upgrade: offer the re-consent the docs require. */}
      <UpgradeReturnBanner />
      {/* "Why upgrade?" explainer shown before any upgrade popup (opened via showUnlock). */}
      <UnlockExplainer />
    </div>
  );
}
